import React, { useMemo, useState } from "react";
import { View, StyleSheet, Pressable, Platform, GestureResponderEvent, StyleProp, ViewStyle } from "react-native";
import { Menu, ActivityIndicator } from "react-native-paper";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { ThreeDot } from "../../svgs/common";
import {
  HeadCircuitIcon,
  ArrowsClockwiseIcon,
  FileTextIcon,
  LinkIcon,
  SparkleIcon,
  FolderUserIcon,
} from "@phosphor-icons/react";
import { ActivityState } from "../../../graphql/generated/graphql";
import DueDatePill from "../Shared/DueDatePill";
import ScheduledPill from "../Shared/ScheduledPill";
import ActivityStatePill from "../Shared/ActivityStatePill";

const TYPE_CONFIG = {
  "Interactive Learning Experience": {
    Icon: SparkleIcon,
  },
  Resource: {
    Icon: LinkIcon,
  },
  Assignment: {
    Icon: FileTextIcon,
  },
  Assessment: {
    Icon: HeadCircuitIcon,
  },
  Review: {
    Icon: ArrowsClockwiseIcon,
  },
  Project: {
    Icon: FolderUserIcon,
  },
};

type ActivityCardItem = {
  id?: string;
  objectives?: Array<{ pendingVideoProcessing?: boolean | null }> | null;
  activityState?: ActivityState | null;
  scheduledDate?: string | Date | null;
  dueDate?: string | Date | null;
  due_date?: string | Date | null;
  isPendingCreation?: boolean;
  processingLabel?: string | null;
};

interface ActivityCardProps {
  title: string;
  type: string;
  item?: ActivityCardItem;
  onOptionsPress: (action: string) => void;
  style?: StyleProp<ViewStyle>;
  onLongPress: (event: GestureResponderEvent) => void;
  onResetILE?: (aiTutorId: string) => void;
  resettingILEId?: string | null;
}

const DragHandle = () => (
  <>
    <View style={styles.dragDots}>
      <View style={styles.dot} />
      <View style={styles.dot} />
    </View>
    <View style={styles.dragDots}>
      <View style={styles.dot} />
      <View style={styles.dot} />
    </View>
  </>
);

const ActivityCard = ({
  title,
  type,
  item,
  onOptionsPress,
  style,
  onLongPress,
  onResetILE,
  resettingILEId,
}: ActivityCardProps) => {
  const config = TYPE_CONFIG[type];
  const Icon = config.Icon;
  const isPendingCreation = item?.isPendingCreation === true;
  const activityState = item?.activityState ?? undefined;
  const dueDate = item?.dueDate ?? item?.due_date ?? null;
  const scheduledDate = item?.scheduledDate ?? null;
  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  const [menuVisible, setMenuVisible] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState({ x: 0, y: 0 });

  const openMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { pageX, pageY } = event.nativeEvent;
    setAnchorPosition({ x: pageX - 100, y: pageY });
    setMenuVisible(true);
  };

  const closeMenu = () => setMenuVisible(false);

  const handleOptionSelect = (action) => {
    if (onOptionsPress) {
      onOptionsPress(action);
    }
    closeMenu();
  };

  if (Platform.OS === "web") {
    window.addEventListener(
      "contextmenu",
      function (e) {
        e.preventDefault();
      },
      true
    );
  }

  const handleDragStart = (e) => {
    if (isPendingCreation) {
      return;
    }
    e.preventDefault();
    onLongPress(e);
  };

  const isInteractiveActivityPendingProcessing: boolean =
    type == "Interactive Learning Experience" &&
    Array.isArray(item?.objectives) &&
    item.objectives.some((objective) => objective.pendingVideoProcessing === true);

  return (
    <View style={[styles.container, style]}>
      <View style={styles.contentContainer}>
        <View style={styles.iconContainer}>{Icon && <Icon size={20} color="#667085" weight="bold" />}</View>
        <View style={styles.textContainer}>
          <View style={styles.titleRow}>
            <CustomText
              text={type === "Assessment" ? "Lesson Assessment" : title}
              useTranslationText={false}
              weight="bold"
              style={styles.title}
              numberOfLines={1}
              ellipsizeMode="tail"
            />
            <View style={styles.pillsRow}>
              {!isModuleLocked && dueDate && <DueDatePill dueDate={dueDate} />}
              {isModuleLocked && scheduledDate && <ScheduledPill scheduledDate={scheduledDate} />}
              {activityState && <ActivityStatePill activityState={activityState} />}
            </View>
          </View>
          {type !== "Assessment" && (
            <CustomText
              text={type}
              useTranslationText={false}
              style={styles.subtitle}
              size="s"
              numberOfLines={1}
              weight="normal"
              ellipsizeMode="tail"
            />
          )}
        </View>
      </View>
      <View style={styles.actionsContainer}>
        {isInteractiveActivityPendingProcessing && <ActivityIndicator />}
        {isPendingCreation && (
          <View style={styles.pendingContainer}>
            <ActivityIndicator size="small" />
            <CustomText
              text={`Creating (${item?.processingLabel || "queued"})`}
              useTranslationText={false}
              size="s"
              style={styles.pendingText}
            />
          </View>
        )}
        {!isPendingCreation && type === "Interactive Learning Experience" && onResetILE && item?.id && (
          <CustomButton
            text="Reset Preview"
            onPress={() => onResetILE(item.id)}
            styleType="primaryTransparentStroke"
            size="s"
            leftIcon={<ArrowsClockwiseIcon size={14} />}
            style={styles.resetButton}
            loading={resettingILEId === item.id}
            disabled={resettingILEId === item.id}
            useTranslationText={false}
            accessibilityLabel="Reset preview progress for this Interactive Learning Experience"
          />
        )}
        {!isPendingCreation && (
          <>
            <Menu
              visible={menuVisible}
              onDismiss={closeMenu}
              anchor={{ x: anchorPosition.x, y: anchorPosition.y }}
              contentStyle={{ backgroundColor: "#FFFFFF", borderRadius: 5 }}>
              {type == "Interactive Learning Experience" && (
                <Menu.Item
                  onPress={() => handleOptionSelect("clone")}
                  title="Clone"
                  titleStyle={{ color: "#344054" }}
                />
              )}
              <Menu.Item
                disabled={isInteractiveActivityPendingProcessing}
                onPress={() => handleOptionSelect("edit")}
                title="Edit"
                titleStyle={{ color: isInteractiveActivityPendingProcessing ? "#d4d4d4ff" : "#344054" }}
              />
              {type !== "Assessment" && (
                <Menu.Item onPress={() => handleOptionSelect("move")} title="Move" titleStyle={{ color: "#344054" }} />
              )}
              <Menu.Item
                onPress={() => handleOptionSelect("delete")}
                title="Delete"
                titleStyle={{ color: "#344054" }}
              />
            </Menu>
            <Pressable
              data-testid="resource-more-options"
              accessibilityRole="button"
              accessibilityLabel="More options"
              aria-label="More options"
              onPress={(e) => openMenu(e)}
              style={styles.optionsButton}>
              <ThreeDot fill="#000" width={20} height={20} />
            </Pressable>
          </>
        )}
      </View>
      {!isPendingCreation && (
        <Pressable onPointerDown={handleDragStart} style={styles.dragHandle}>
          <DragHandle />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#EAECF0",
    marginVertical: 4,
    width: "100%",
    position: "relative",
  },
  contentContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flex: 1,
  },
  iconContainer: {
    marginRight: 8,
  },
  textContainer: {
    flex: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
    minWidth: 0,
    marginBottom: 2,
  },
  title: {
    color: "#344054",
    flexShrink: 1,
    minWidth: 0,
    marginRight: 16,
  },
  subtitle: {
    color: "#667085",
  },
  pillsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    flexShrink: 0,
  },

  actionsContainer: {
    marginLeft: 12,
    flexDirection: "row",
    alignItems: "center",
  },
  pendingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginRight: 8,
  },
  pendingText: {
    color: "#667085",
  },
  optionsButton: {
    padding: 4,
    marginRight: 8,
  },
  dragHandle: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    flexDirection: "row",
    width: 20,
    justifyContent: "center",
    gap: 2,
    alignItems: "center",
    marginLeft: 8,
    backgroundColor: "#ccc",
  },
  dragDots: {
    justifyContent: "center",
    height: 12,
    gap: 2,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: "#9CA3AF",
    marginVertical: 1,
  },
  resetButton: {
    marginRight: 8,
  },
});

export default ActivityCard;
